import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import MenuBookIcon from '@mui/icons-material/MenuBook';

import { useTranslation } from 'react-i18next';
import ParametersDialog from './components/ParametersDialog';
import HighResDialog from './components/HighResDialog';
import { defaultSettings, solowSmithReference } from './constants';
import ChartPanel from './components/ChartPanel';
import ForestPlot from './components/ForestPlot';

import RecommendPanel from './components/RecommendPanel';
import ResultsPanel from './components/ResultsPanel';
import OutputDialog from './components/OutputDialog';
import { useBaseFit } from './hooks/useBaseFit';
import { useConceptRecords } from './hooks/useConceptRecords';
import { useDiscoverySeries } from './hooks/useDiscoverySeries';
import { useHighResFit } from './hooks/useHighResFit';
import { useRecommendation } from './hooks/useRecommendation';
import { settingsReducer } from './hooks/useSettingsReducer';
import { useTaxonomyData } from './hooks/useTaxonomyData';
import { gompertzCurve } from './models/gompertz';
import { logisticCurve } from './models/logistic';
import { solowSmithP } from './models/solowSmith';
import {
  buildCumulativePointsFromCounts,
  buildDiscoverySeries,
  countNonZeroYears,
  computeSpanYears,
  filterDiscoveryCounts,
  inferFirstDiscoveryYear,
} from './series/discoverySeries';
import { normalizeRank } from './taxonomy/normalize';
import {
  formatCI,
  formatDate,
  formatDecimal,
  formatDeltaWithPercent,
  formatNumber,
  formatPercent,
  formatPValue,
  formatTooltipValue,
} from './utils/format';
import { createSeededRandom, hashSeed } from './utils/random';

const TotalSpeciesEstimation = () => {
  const { t } = useTranslation(['analysis', 'common']);
  const [settings, dispatch] = useReducer(settingsReducer, defaultSettings);
  const {
    filters,
    bootstrap,
    chart,
    forest,
    highRes,
    recommend,
  } = settings;

  const {
    rawData,
    loading,
    error,
    rankOptions,
    unitOptions,
    recordMap,
    familyCache,
    subfamilyCache,
    yearRange,
  } = useTaxonomyData(t);

  useEffect(() => {
    if (yearRange[0] !== 0 && filters.startYear === 0) {
      dispatch({
        type: 'setFilters',
        payload: {
          startYear: yearRange[0],
          endYear: yearRange[1],
        },
      });
    }
  }, [yearRange, filters.startYear]);

  useEffect(() => {
    if (rankOptions.length > 0 && filters.rankFilter === 'All') {
      const preferred = rankOptions.find(rank => (rank || '').toLowerCase() === 'species');
      if (preferred) dispatch({ type: 'setFilters', payload: { rankFilter: preferred } });
    }
  }, [rankOptions, filters.rankFilter]);

  useEffect(() => {
    if (filters.conceptUnit === 'acceptedOnly' && filters.statusFilter !== 'Valid') {
      dispatch({ type: 'setFilters', payload: { statusFilter: 'Valid' } });
    }
  }, [filters.conceptUnit, filters.statusFilter]);

  const conceptRecords = useConceptRecords({
    rawData,
    recordMap,
    familyCache,
    subfamilyCache,
    conceptUnit: filters.conceptUnit,
  });

  const yearOptions = useMemo(() => {
    if (yearRange[0] === 0) return [];
    const options = [];
    for (let year = yearRange[0]; year <= yearRange[1]; year += 1) {
      options.push(year);
    }
    return options;
  }, [yearRange]);

  const unitParts = useMemo(() => (filters.unitFilter === 'All' ? [] : filters.unitFilter.split('::')), [filters.unitFilter]);
  const unitType = unitParts[0];
  const unitName = unitParts.slice(1).join('::');

  const targetUnitLabel = useMemo(() => {
    if (filters.unitFilter === 'All') return t('common:all');
    if (unitType === 'family') return `${t('common:family')}: ${unitName}`;
    if (unitType === 'subfamily') return `${t('common:subfamily')}: ${unitName}`;
    return filters.unitFilter;
  }, [filters.unitFilter, unitType, unitName, t]);

  const filteredRecords = useMemo(() => {
    if (filters.startYear === 0 || filters.endYear === 0) return [];
    const normalizedRankFilter = normalizeRank(filters.rankFilter);
    return conceptRecords.filter(item => {
      const rankMatch = filters.rankFilter === 'All' || item.rank === normalizedRankFilter;
      const unitMatch = filters.unitFilter === 'All'
        || (unitType === 'family' && item.family === unitName)
        || (unitType === 'subfamily' && item.subfamily === unitName);
      const statusMatch = filters.statusFilter === 'All'
        || (filters.statusFilter === 'Valid' && item.status === 'valid')
        || (filters.statusFilter === 'Invalid' && item.status !== 'valid');
      
      // Context filter logic
      // 'neontological' includes pure neontological AND mixed (extant with fossils)
      // 'paleontological' includes pure paleontological AND mixed (fossil with extant)
      // 'unknown' includes only unknown
      const contextMatch = filters.contextFilter === 'All'
        || (filters.contextFilter === 'neontological' && (item.context === 'neontological' || item.context === 'mixed'))
        || (filters.contextFilter === 'paleontological' && (item.context === 'paleontological' || item.context === 'mixed'))
        || (filters.contextFilter === 'unknown' && item.context === 'unknown');

      const year = Number(item.year);
      const yearMatch = Number.isFinite(year) && year >= filters.startYear && year <= filters.endYear;
      return rankMatch && unitMatch && statusMatch && contextMatch && yearMatch;
    });
  }, [
    conceptRecords,
    filters.rankFilter,
    filters.unitFilter,
    unitType,
    unitName,
    filters.statusFilter,
    filters.contextFilter,
    filters.startYear,
    filters.endYear,
  ]);

  const {
    chartData,
    currentCount,
    modelStartYear,
    modelDiscoveryCounts,
    modelFitPoints,
    modelSolowPoints,
  } = useDiscoverySeries({
    filteredRecords,
    startYear: filters.startYear,
    endYear: filters.endYear,
  });

  const subfamilyGroups = useMemo(() => {
    const groups = new Map();
    filteredRecords.forEach((item, index) => {
      const key = item.subfamily || '__unassigned__';
      const list = groups.get(key);
      if (list) {
        list.push(index);
      } else {
        groups.set(key, [index]);
      }
    });
    return groups;
  }, [filteredRecords]);

  const groupStats = useMemo(() => (
    Array.from(subfamilyGroups.entries()).map(([key, recordIndexes]) => {
      const records = recordIndexes.map(index => filteredRecords[index]);
      const series = buildDiscoverySeries(records, filters.startYear, filters.endYear);
      const modelStart = inferFirstDiscoveryYear(series.discoveryCounts) ?? filters.startYear;
      const modelDiscovery = filterDiscoveryCounts(series.discoveryCounts, modelStart);
      const modelFitPointsLocal = buildCumulativePointsFromCounts(modelDiscovery, modelStart);
      return {
        key,
        recordIndexes,
        series,
        modelStartYear: modelStart,
        modelDiscoveryCounts: modelDiscovery,
        modelFitPoints: modelFitPointsLocal,
        eventCount: series.currentCount,
        nonZeroYears: countNonZeroYears(series.discoveryCounts),
        spanYears: computeSpanYears(records, filters.startYear, filters.endYear),
      };
    })
  ), [subfamilyGroups, filteredRecords, filters.startYear, filters.endYear]);

  const getRandomFn = useCallback(() => {
    const seedText = (bootstrap.randomSeed ?? '').toString().trim();
    if (!seedText) return Math.random;
    return createSeededRandom(hashSeed(seedText));
  }, [bootstrap.randomSeed]);

  const baseFit = useBaseFit({
    modelFitPoints,
    modelSolowPoints,
    modelDiscoveryCounts,
    modelStartYear,
    endYear: filters.endYear,
    bootstrapSamples: bootstrap.bootstrapSamples,
    curveBandEnabled: bootstrap.curveBandEnabled,
    curveFitMethod: bootstrap.curveFitMethod,
    randomSeed: bootstrap.randomSeed,
    getRandomFn,
    t,
  });

  const fitResults = baseFit.result;

  const chartRef = useRef(null);
  const [outputOpen, setOutputOpen] = useState(false);
  const [parametersDialogOpen, setParametersDialogOpen] = useState(false);
  const [highResDialogOpen, setHighResDialogOpen] = useState(false);

  const chartDataWithModels = useMemo(() => {
    if (!chartData.length) return [];
    const logisticParams = fitResults?.logistic?.params;
    const gompertzParams = fitResults?.gompertz?.params;
    const logisticCI = fitResults?.logistic?.ci;
    const gompertzCI = fitResults?.gompertz?.ci;
    const solowSmith = fitResults?.solowSmith;
    const solowParams = solowSmith?.params;
    const solowTotal = solowSmith?.total;
    const solowCI = solowSmith?.ci;
    const solowBootstrapCI = solowSmith?.bootstrapCI;
    const effectiveModelStart = Number.isFinite(fitResults?.modelStartYear)
      ? fitResults.modelStartYear
      : modelStartYear;
    const logisticCurveBandMap = logisticCI?.curveBand && logisticCI?.curveYears
      ? new Map(logisticCI.curveYears.map((year, index) => [year, logisticCI.curveBand[index]]))
      : null;
    const gompertzCurveBandMap = gompertzCI?.curveBand && gompertzCI?.curveYears
      ? new Map(gompertzCI.curveYears.map((year, index) => [year, gompertzCI.curveBand[index]]))
      : null;
    const solowCurveBandMap = solowBootstrapCI?.curveBand && solowBootstrapCI?.curveYears
      ? new Map(solowBootstrapCI.curveYears.map((year, index) => [year, solowBootstrapCI.curveBand[index]]))
      : null;

    return chartData.map(point => {
      const nextPoint = { ...point };
      const hasModelStart = Number.isFinite(effectiveModelStart);
      const tYear = hasModelStart ? point.year - effectiveModelStart : null;
      const isAfterStart = hasModelStart && tYear >= 0;

      if (logisticParams && isAfterStart) {
        nextPoint.logistic = logisticCurve(logisticParams, tYear);
        nextPoint.logisticSaturation = logisticParams.L;
      }
      if (gompertzParams && isAfterStart) {
        nextPoint.gompertz = gompertzCurve(gompertzParams, tYear);
        nextPoint.gompertzSaturation = gompertzParams.a;
      }
      if (logisticCurveBandMap && isAfterStart) {
        const range = logisticCurveBandMap.get(point.year);
        if (range) nextPoint.logisticRange = range;
      } else if (logisticParams && logisticCI && Number.isFinite(logisticCI.lower) && Number.isFinite(logisticCI.upper) && isAfterStart) {
        const lower = logisticCurve({ ...logisticParams, L: logisticCI.lower }, tYear);
        const upper = logisticCurve({ ...logisticParams, L: logisticCI.upper }, tYear);
        nextPoint.logisticRange = [Math.min(lower, upper), Math.max(lower, upper)];
      }
      if (gompertzCurveBandMap && isAfterStart) {
        const range = gompertzCurveBandMap.get(point.year);
        if (range) nextPoint.gompertzRange = range;
      } else if (gompertzParams && gompertzCI && Number.isFinite(gompertzCI.lower) && Number.isFinite(gompertzCI.upper) && isAfterStart) {
        const lower = gompertzCurve({ ...gompertzParams, a: gompertzCI.lower }, tYear);
        const upper = gompertzCurve({ ...gompertzParams, a: gompertzCI.upper }, tYear);
        nextPoint.gompertzRange = [Math.min(lower, upper), Math.max(lower, upper)];
      }
      if (solowParams && Number.isFinite(solowTotal) && isAfterStart) {
        const P = solowSmithP(tYear, solowParams.h, solowParams.b);
        if (Number.isFinite(P)) {
          nextPoint.solowSmith = solowTotal * P;
          nextPoint.solowSmithSaturation = solowTotal;
          if (solowCurveBandMap) {
            const range = solowCurveBandMap.get(point.year);
            if (range) nextPoint.solowSmithRange = range;
          } else if (solowCI && Number.isFinite(solowCI.lower) && Number.isFinite(solowCI.upper)) {
            const lower = solowCI.lower * P;
            const upper = solowCI.upper * P;
            nextPoint.solowSmithRange = [Math.min(lower, upper), Math.max(lower, upper)];
          }
        }
      }
      return nextPoint;
    });
  }, [chartData, fitResults, modelStartYear]);

  const tooltipFormatter = (value, name) => [formatTooltipValue(value), name];

  const logisticAvailable = Boolean(fitResults?.logistic?.params);
  const gompertzAvailable = Boolean(fitResults?.gompertz?.params);
  const solowSmithAvailable = Boolean(fitResults?.solowSmith?.params);
  const logisticCurveBandAvailable = Boolean(fitResults?.logistic?.ci?.curveBand?.length);
  const gompertzCurveBandAvailable = Boolean(fitResults?.gompertz?.ci?.curveBand?.length);
  const solowCurveBandAvailable = Boolean(fitResults?.solowSmith?.bootstrapCI?.curveBand?.length);
  const logisticBandAvailable = logisticCurveBandAvailable || Boolean(
    fitResults?.logistic?.ci
    && Number.isFinite(fitResults.logistic.ci.lower)
    && Number.isFinite(fitResults.logistic.ci.upper)
  );
  const gompertzBandAvailable = gompertzCurveBandAvailable || Boolean(
    fitResults?.gompertz?.ci
    && Number.isFinite(fitResults.gompertz.ci.lower)
    && Number.isFinite(fitResults.gompertz.ci.upper)
  );
  const solowSmithBandAvailable = solowCurveBandAvailable || Boolean(
    fitResults?.solowSmith?.ci
    && Number.isFinite(fitResults.solowSmith.ci.lower)
    && Number.isFinite(fitResults.solowSmith.ci.upper)
  );

  const highResFit = useHighResFit({
    groupStats,
    filteredRecords,
    startYear: filters.startYear,
    endYear: filters.endYear,
    unitType,
    highResSettings: highRes,
    curveFitMethod: bootstrap.curveFitMethod,
    randomSeed: bootstrap.randomSeed,
    getRandomFn,
    t,
  });

  const recommendation = useRecommendation({
    groupStats,
    endYear: filters.endYear,
    unitType,
    filteredCount: filteredRecords.length,
    minGroupEvents: highRes.minGroupEvents,
    minActiveYears: highRes.minActiveYears,
    minSpanYears: highRes.minSpanYears,
    solowGuardEnabled: highRes.solowGuardEnabled,
    solowMaxRatio: highRes.solowMaxRatio,
    solowMinR2: highRes.solowMinR2,
    curveGuardEnabled: highRes.curveGuardEnabled,
    curveMinR2: highRes.curveMinR2,
    recommendBootstrapSamples: recommend.recommendBootstrapSamples,
    curveFitMethod: bootstrap.curveFitMethod,
    randomSeed: bootstrap.randomSeed,
    getRandomFn,
    t,
  });

  const applyRecommendation = useCallback((rec) => {
    if (!rec) return;
    dispatch({
      type: 'setHighRes',
      payload: {
        minGroupEvents: rec.minEvents,
        minActiveYears: rec.minNonZeroYears,
        minSpanYears: rec.minSpanYears,
      },
    });
  }, []);

  const recommendPanelNode = (
    <RecommendPanel
      t={t}
      loading={recommendation.loading}
      error={recommendation.error}
      stage={recommendation.stage}
      progress={recommendation.progress}
      canRecommend={recommendation.canRecommend}
      disabledReason={recommendation.disabledReason}
      fastResult={recommendation.fastResult}
      bootstrapResult={recommendation.bootstrapResult}
      recommendBootstrapSamples={recommend.recommendBootstrapSamples}
      onRecommendBootstrapSamplesChange={(event) => {
        const value = Number(event.target.value);
        if (!Number.isFinite(value)) {
          dispatch({ type: 'setRecommend', payload: { recommendBootstrapSamples: 0 } });
          return;
        }
        const clamped = Math.max(10, Math.min(200, Math.round(value)));
        dispatch({ type: 'setRecommend', payload: { recommendBootstrapSamples: clamped } });
      }}
      onRunFast={() => recommendation.runRecommendation('fast')}
      onRunBootstrap={() => recommendation.runRecommendation('bootstrap')}
      onApplyRecommendation={applyRecommendation}
      formatNumber={formatNumber}
      formatPercent={formatPercent}
    />
  );

  // Load saved settings from localStorage or from URL (?tse=...)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tseParam = params.get('tse');
      if (tseParam) {
        try {
          const decoded = JSON.parse(atob(decodeURIComponent(tseParam)));
          if (decoded && typeof decoded === 'object') {
            dispatch({ type: 'loadSettings', payload: decoded });
          }
        } catch (err) {
          // ignore malformed param
        }
        return;
      }
      const raw = localStorage.getItem('TSE_settings_v1');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          dispatch({ type: 'loadSettings', payload: parsed });
        }
      }
    } catch (err) {
      // ignore errors
    }
  }, []);

  const baseTotals = {
    solow: fitResults?.solowSmith?.total ?? null,
    logistic: fitResults?.logistic?.params?.L ?? null,
    gompertz: fitResults?.gompertz?.params?.a ?? null,
  };

  const deltas = {
    solow: highResFit.results?.solowSmith && Number.isFinite(baseTotals.solow)
      ? highResFit.results.solowSmith.total - baseTotals.solow
      : null,
    logistic: highResFit.results?.logistic && Number.isFinite(baseTotals.logistic)
      ? highResFit.results.logistic.total - baseTotals.logistic
      : null,
    gompertz: highResFit.results?.gompertz && Number.isFinite(baseTotals.gompertz)
      ? highResFit.results.gompertz.total - baseTotals.gompertz
      : null,
  };

  const forestHighResAvailable = Boolean(
    highResFit.results?.solowSmith || highResFit.results?.logistic || highResFit.results?.gompertz
  );

  const titleSx = {
    fontWeight: 800,
    letterSpacing: '0.01em',
    color: 'text.primary',
    textAlign: 'center',
    mb: 1,
  };

  const subtitleSx = {
    textAlign: 'center',
    whiteSpace: 'pre-line',
    color: 'text.secondary',
    maxWidth: 820,
    mx: 'auto',
    mb: 4,
  };

  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Container maxWidth="xl">
        <Typography variant="h2" sx={titleSx}>
          {t('estimationPage.title')}
        </Typography>
        <Typography variant="body1" sx={subtitleSx}>
          {t('estimationPage.description')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => setParametersDialogOpen(true)}
            aria-label={t('estimationPage.editParameters') || 'Edit parameters'}
            sx={{ textTransform: 'none', borderRadius: 8 }}
          >
            {t('estimationPage.editParameters') || 'パラメータ設定'}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => setHighResDialogOpen(true)}
            aria-label={t('estimationPage.runHighRes') || 'Run high-res'}
            sx={{ textTransform: 'none', borderRadius: 8 }}
          >
            {t('estimationPage.runHighRes') || '高精細計算'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<SaveIcon />}
            onClick={() => setOutputOpen(true)}
            aria-label={t('estimationPage.outputResult') || 'Output Result'}
            sx={{ textTransform: 'none', borderRadius: 8, boxShadow: 'none' }}
          >
            {t('estimationPage.outputResult') || 'Output Result'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            <Typography variant="h6">{t('error.title')}</Typography>
            <Typography>{error}</Typography>
            <Typography sx={{ mt: 1 }}>
              <strong>{t('error.adminHintTitle')}</strong> {t('error.adminHintBody')}
            </Typography>
          </Alert>
        )}

        {!error && (
          <>


            {/* Parameters are now edited via dialog */}
            <ParametersDialog
              open={parametersDialogOpen}
              onClose={() => setParametersDialogOpen(false)}
              t={t}
              unitOptions={unitOptions}
              rankOptions={rankOptions}
              yearOptions={yearOptions}
              settings={settings}
              dispatch={dispatch}
            />

            {/* High-res is executed via dialog */}
            <HighResDialog
              open={highResDialogOpen}
              onClose={() => setHighResDialogOpen(false)}
              t={t}
              settings={settings}
              dispatch={dispatch}
              highResFit={highResFit}
              baseTotals={baseTotals}
              deltas={deltas}
              formatNumber={formatNumber}
              formatCI={formatCI}
              formatDeltaWithPercent={formatDeltaWithPercent}
              recommendPanel={recommendPanelNode}
              onRunHighRes={() => {
                if (typeof highResFit.run === 'function') highResFit.run();
              }}
            />



            <OutputDialog
              open={outputOpen}
              onClose={() => setOutputOpen(false)}
              settings={settings}
              chartRef={chartRef}
              chartData={chartDataWithModels}
              fitResults={fitResults}
            />

            <ForestPlot
              t={t}
              fitResults={fitResults}
              highResResults={highResFit.results}
              currentCount={currentCount}
              includeHighRes={forest.includeHighRes}
              intervalMode={forest.solowIntervalMode}
              sortOrder={forest.sortOrder}
              onIncludeHighResChange={(value) => dispatch({ type: 'setForest', payload: { includeHighRes: value } })}
              onIntervalModeChange={(value) => dispatch({ type: 'setForest', payload: { solowIntervalMode: value } })}
              onSortOrderChange={(value) => dispatch({ type: 'setForest', payload: { sortOrder: value } })}
              highResAvailable={forestHighResAvailable}
              loading={loading || baseFit.loading || highResFit.loading}
              error={baseFit.error}
              formatNumber={formatNumber}
              fitStage={baseFit.loading ? baseFit.stage : highResFit.stage}
              fitProgress={baseFit.loading ? baseFit.progress : highResFit.progress}
              highResSettings={highRes}
              targetUnitLabel={targetUnitLabel}
            />

            <ResultsPanel
              t={t}
              loading={loading}
              targetUnitLabel={targetUnitLabel}
              currentCount={currentCount}
              fitResults={fitResults}
              highResResults={highResFit.results}
              highResSettings={highRes}
              baseTotals={baseTotals}
              deltas={deltas}
              fitLoading={baseFit.loading || highResFit.loading}
              fitError={baseFit.error}
              fitStage={baseFit.loading ? baseFit.stage : highResFit.stage}
              fitProgress={baseFit.loading ? baseFit.progress : highResFit.progress}
              formatNumber={formatNumber}
              formatDecimal={formatDecimal}
              formatCI={formatCI}
              formatPValue={formatPValue}
              formatPercent={formatPercent}
              formatDeltaWithPercent={formatDeltaWithPercent}
            />

            {loading ? (
              chart.showChart && <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 2, mb: 3 }} />
            ) : (
              chart.showChart && (
              <ChartPanel
                t={t}
                chartDataWithModels={chartDataWithModels}
                tooltipFormatter={tooltipFormatter}
                showSolowSmith={chart.showSolowSmith}
                showLogistic={chart.showLogistic}
                showGompertz={chart.showGompertz}
                onToggleSolowSmith={(value) => dispatch({ type: 'setChart', payload: { showSolowSmith: value } })}
                onToggleLogistic={(value) => dispatch({ type: 'setChart', payload: { showLogistic: value } })}
                onToggleGompertz={(value) => dispatch({ type: 'setChart', payload: { showGompertz: value } })}
                solowSmithAvailable={solowSmithAvailable}
                logisticAvailable={logisticAvailable}
                gompertzAvailable={gompertzAvailable}
                solowSmithBandAvailable={solowSmithBandAvailable}
                logisticBandAvailable={logisticBandAvailable}
                gompertzBandAvailable={gompertzBandAvailable}
                solowCurveBandAvailable={solowCurveBandAvailable}
                logisticCurveBandAvailable={logisticCurveBandAvailable}
                gompertzCurveBandAvailable={gompertzCurveBandAvailable}
                fitLoading={baseFit.loading}
                fitStage={baseFit.stage}
                fitProgress={baseFit.progress}
                chartRef={chartRef}
              />
              )
            )}

            {loading ? (
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
            ) : (
              <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 3, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MenuBookIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">{t('estimationPage.referenceTitle')}</Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {solowSmithReference.title}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('estimationPage.referenceIdLabel')}</Typography>
                    <Typography>{solowSmithReference.id}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('estimationPage.referenceDateLabel')}</Typography>
                    <Typography>{formatDate(solowSmithReference.publicationDate)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('estimationPage.referenceVolumeLabel')}</Typography>
                    <Typography>{solowSmithReference.volume}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('estimationPage.referenceIssueLabel')}</Typography>
                    <Typography>{solowSmithReference.issue}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('estimationPage.referencePagesLabel')}</Typography>
                    <Typography>{solowSmithReference.pages}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('estimationPage.referenceDoiLabel')}</Typography>
                    <Link href={`https://doi.org/${solowSmithReference.doi}`} target="_blank" rel="noreferrer">
                      {solowSmithReference.doi}
                    </Link>
                  </Box>
                </Box>
              </Paper>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default TotalSpeciesEstimation;
